import * as Sentry from "@sentry/react";
import { ProblemDetailsError } from "@app/Api/ApiHelpers";
import { EFxIdSdkAdapterBuyProductResponseErrorCode } from "./FxIdSdkBaseAdapter";

/** Платформа, внутри которой игра покупает товар (Platform purchase, #fxid-1856) */
export type PlatformPurchasePlatform = "vkcom" | "ok" | "vkplay" | "yandex-games";

/** Шаг платформенной покупки, на котором она оборвалась */
export type PlatformPurchaseStep =
	// Яндекс: покупка запрещена, пока открыт диалог выбора аккаунта
	| "account_selection"
	// Яндекс: ysdk.getPlayer
	| "get_player"
	// Яндекс: ysdk.getPayments
	| "get_payments"
	// FX ID: создание заказа
	| "create_payment"
	// Окно оплаты платформы
	| "purchase"
	// FX ID: проверка подписанной покупки (Яндекс)
	| "verify"
	// Яндекс: payments.consumePurchase
	| "consume";

/** Шаги, ошибку которых даёт FX ID (наш API или наша проверка); ошибку остальных шагов даёт SDK платформы */
const FXID_STEPS: ReadonlySet<PlatformPurchaseStep> = new Set<PlatformPurchaseStep>([
	"account_selection",
	"create_payment",
	"verify"
]);

/**
 * Источник ошибки (Error source): FX ID или конкретный SDK платформы, через который шёл шаг.
 * Не совпадает с платформой: Одноклассники доступны через FAPI или через VK Bridge (кабинет ВК)
 */
export type PlatformPurchaseErrorSource = "fxid" | "vk-bridge" | "ok-fapi" | "vkplay" | "yandex-games-sdk";

/** Правила платформы: как назвать её ошибку, распознать отмену и сгруппировать сбой в Sentry */
export interface IPlatformPurchaseDescriptor {
	/** Платформа покупки */
	readonly platform: PlatformPurchasePlatform;
	/** SDK платформы, выдавший ошибку */
	readonly source: Exclude<PlatformPurchaseErrorSource, "fxid">;
	/**
	 * Отменил ли покупку игрок: отмена приходит игре с errorCode UserCancelled и не уходит в Sentry
	 * @param step шаг платформы, на котором пришла ошибка
	 * @param error исходная ошибка шага
	 */
	isCancellation(step: PlatformPurchaseStep, error: unknown): boolean;
	/**
	 * Текст ошибки SDK платформы, если его нельзя взять из Error.message или поля message (например ErrorData VK Bridge)
	 * @param error исходная ошибка шага
	 */
	errorText?(error: unknown): string | undefined;
	/**
	 * Код ошибки SDK платформы, если он лежит не в поле code
	 * @param error исходная ошибка шага
	 */
	sourceCode?(error: unknown): string | undefined;
}

/** Что известно о покупке к началу шага */
export interface IPlatformPurchaseContext {
	/** SKU товара, который покупает игра */
	sku: string;
	/** Номер заказа FX ID; появляется после шага create_payment */
	transactionId?: string;
}

/**
 * Диагностика отмены или сбоя платформенной покупки (#fxid-1856, ADR 0014).
 * Игра получает объект в FxIdMessage.errorDetails и в details отклонённой ошибки,
 * а message ошибки — JSON.stringify этого же объекта. Незнакомые поля игра должна игнорировать
 */
export interface PlatformPurchaseErrorDetails {
	/** Текст исходной ошибки */
	message: string;
	/** UserCancelled — игрок отменил покупку (Purchase cancellation), Unknown — сбой (Purchase failure) */
	errorCode: EFxIdSdkAdapterBuyProductResponseErrorCode;
	/** Платформа покупки */
	platform: PlatformPurchasePlatform;
	/** Шаг, на котором оборвалась покупка */
	step: PlatformPurchaseStep;
	/** Кто выдал ошибку */
	source: PlatformPurchaseErrorSource;
	/** Код ошибки источника строкой (HTTP-статус FX ID, код SDK платформы); нет, если источник его не дал */
	code?: string;
	/** SKU товара из запроса игры */
	sku: string;
	/** Номер заказа FX ID; нет, если заказ ещё не создан */
	transactionId?: string;
	/** Исходная ошибка источника, сериализуемая в JSON без потерь */
	cause: Record<string, unknown>;
}

/**
 * Текст для отказа значением, которое не является Error (SDK может реджектить строкой или plain-объектом)
 * @param value значение, которым отклонён промис
 */
function stringifyRejection(value: unknown): string {
	if (typeof value === "object" && value !== null) {
		try {
			return JSON.stringify(value);
		} catch {
			// Циклический объект не сериализуется в JSON — остаётся только String()
		}
	}
	return String(value);
}

/**
 * Переводит исходную ошибку в сериализуемый объект, не теряя полей, нужных для диагностики.
 * errorToString в канале с игрой берёт из Error только message, поэтому code и httpStatus копируются явно,
 * ProblemDetailsError отдаёт весь ответ API, а plain-объект (ErrorData VK Bridge, ответ FAPI) передаётся целиком.
 * @param error исходная ошибка шага
 */
function describeError(error: unknown): Record<string, unknown> {
	let cause: Record<string, unknown>;
	if (error instanceof ProblemDetailsError) {
		cause = error.details;
	} else if (error instanceof Error) {
		cause = {
			name: error.name,
			message: error.message,
			...("code" in error ? { code: error.code } : {}),
			...("httpStatus" in error ? { httpStatus: error.httpStatus } : {})
		};
	} else if (typeof error === "object" && error !== null) {
		cause = error as Record<string, unknown>;
	} else {
		return { message: String(error) };
	}
	try {
		return JSON.parse(JSON.stringify(cause)) as Record<string, unknown>;
	} catch {
		// Циклический объект не сериализуется в JSON — остаётся только String()
		return { message: String(error) };
	}
}

/**
 * Текст исходной ошибки: message у Error и у plain-объекта с полем message, иначе сериализованное значение
 * @param error исходная ошибка шага
 */
function describeErrorText(error: unknown): string {
	if (error instanceof Error) {
		return error.message;
	}
	if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") {
		return error.message;
	}
	return stringifyRejection(error);
}

/**
 * Приводит числовой или непустой строковый код источника к публичному формату
 * @param value код SDK платформы или HTTP-статус FX ID
 */
function sourceErrorCode(value: unknown): string | undefined {
	return typeof value === "number" || (typeof value === "string" && value.length > 0) ? String(value) : undefined;
}

/**
 * Сбой или отмена платформенной покупки с полной диагностикой (#fxid-1856).
 * Игра получает один объект в details и JSON того же объекта в error.message.
 */
export class PlatformPurchaseError extends Error {
	/** Диагностика, сериализованная в message */
	readonly details: PlatformPurchaseErrorDetails;
	/** Исходная ошибка SDK платформы или API FX ID — уходит в Sentry как есть */
	readonly originalError: unknown;
	/** Покупку отменил игрок: в Sentry не отправляется */
	readonly isCancellation: boolean;

	/**
	 * @param details диагностика сбоя
	 * @param originalError исходная ошибка или ответ, из-за которого покупка оборвалась
	 */
	constructor(details: PlatformPurchaseErrorDetails, originalError: unknown) {
		super(JSON.stringify(details));
		this.name = "PlatformPurchaseError";
		this.details = details;
		this.originalError = originalError;
		this.isCancellation = details.errorCode === EFxIdSdkAdapterBuyProductResponseErrorCode.UserCancelled;
		Object.setPrototypeOf(this, PlatformPurchaseError.prototype);
	}

	/**
	 * Собирает единый объект диагностики с платформой, источником и JSON-клоном исходной ошибки
	 * @param descriptor правила платформы
	 * @param step шаг покупки
	 * @param context SKU и номер заказа, известные к началу шага
	 * @param error исходная ошибка шага
	 */
	static fromError(
		descriptor: IPlatformPurchaseDescriptor,
		step: PlatformPurchaseStep,
		context: IPlatformPurchaseContext,
		error: unknown
	): PlatformPurchaseError {
		const fromFxId = FXID_STEPS.has(step);
		const isCancellation = !fromFxId && descriptor.isCancellation(step, error);
		const platformText = fromFxId ? undefined : descriptor.errorText?.(error);
		const cause = describeError(error);
		const code = fromFxId
			? sourceErrorCode(cause.status)
			: descriptor.sourceCode?.(error) ?? sourceErrorCode(cause.code);
		const details: PlatformPurchaseErrorDetails = {
			message: platformText ?? describeErrorText(error),
			errorCode: isCancellation
				? EFxIdSdkAdapterBuyProductResponseErrorCode.UserCancelled
				: EFxIdSdkAdapterBuyProductResponseErrorCode.Unknown,
			platform: descriptor.platform,
			step,
			source: fromFxId ? "fxid" : descriptor.source,
			...(code === undefined ? {} : { code }),
			sku: context.sku,
			...(context.transactionId === undefined ? {} : { transactionId: context.transactionId }),
			cause
		};
		return new PlatformPurchaseError(details, error);
	}
}

/**
 * Выполняет шаг покупки и превращает его ошибку в PlatformPurchaseError с шагом и контекстом заказа
 * @param descriptor правила платформы
 * @param step шаг покупки
 * @param context SKU и номер заказа, известные к началу шага
 * @param action вызов SDK платформы или API FX ID
 */
export async function runPlatformPurchaseStep<T>(
	descriptor: IPlatformPurchaseDescriptor,
	step: PlatformPurchaseStep,
	context: IPlatformPurchaseContext,
	action: () => Promise<T>
): Promise<T> {
	try {
		return await action();
	} catch (error) {
		throw PlatformPurchaseError.fromError(descriptor, step, context, error);
	}
}

/**
 * Отправляет сбой покупки в Sentry (#fxid-1856). Отмены игроком не отправляются.
 * Группа задаётся платформой, шагом и кодом явно: иначе все сбои собрались бы в одну группу по общему стеку вызова.
 * @param error сбой покупки
 * @param game системное имя игры
 */
export function capturePlatformPurchaseError(error: PlatformPurchaseError, game: string): void {
	if (error.isCancellation) {
		return;
	}
	const { platform, step } = error.details;
	const code = error.details.code ?? "NO_CODE";

	Sentry.captureException(new Error(`${platform} purchase failed at ${step}: ${code}`), {
		fingerprint: [`${platform}-purchase`, step, code],
		tags: {
			"purchase.platform": platform,
			"purchase.step": step,
			"purchase.error_code": code,
			game
		},
		contexts: { purchase: { ...error.details } },
		extra: { rawError: error.originalError }
	});
}
